import type {
  BrowserImageLease,
  BrowserImageRenderAsset,
} from './image-store'
import {
  GRID_COLS_MAX,
  GRID_IMAGE_MAX_DIMENSION,
  GRID_IMAGE_MAX_PLACEMENTS,
  GRID_ROWS_MAX,
  type DecodedImagePlacement,
  type DecodedImageSceneRecord,
} from './protocol'

export interface ImageGeometryAssetSource {
  readonly activeEpoch?: number
  acquireForRender(assetId: number): BrowserImageLease<ImageGeometryRenderAsset> | null
}

export type ImageGeometryRenderAsset = BrowserImageRenderAsset | Readonly<{
  assetId: number
  contentHash: string
  width: number
  height: number
  rendererRepresentation: object
}>

export interface ImageGeometryMetrics {
  readonly cols: number
  readonly rows: number
  readonly cellWidth: number
  readonly cellHeight: number
  readonly viewportWidth: number
  readonly viewportHeight: number
}

export interface BuildImageGeometryOptions {
  // Canvas rendering can suspend on glyph rasterization before drawImage, so
  // it retains sources through the private staging transaction. WebGL probes
  // readiness here and reacquires one bitmap at a time before texture upload.
  readonly retainSources?: boolean
}

export interface ImageDraw {
  readonly assetId: number
  readonly contentHash: string
  readonly source?: ImageBitmap
  readonly rendererRepresentation?: object
  readonly sourceWidthPixels: number
  readonly sourceHeightPixels: number
  readonly sourceX: number
  readonly sourceY: number
  readonly sourceWidth: number
  readonly sourceHeight: number
  readonly u0: number
  readonly v0: number
  readonly u1: number
  readonly v1: number
  readonly destinationX: number
  readonly destinationY: number
  readonly destinationWidth: number
  readonly destinationHeight: number
  readonly filter: 'nearest' | 'linear'
  readonly order: number
}

export interface ImageGeometry {
  readonly draws: readonly ImageDraw[]
  readonly suppression: Uint8Array
  release(): void
}

export function validateImageDraws(
  draws: readonly ImageDraw[], options: Readonly<{ requireSource?: boolean }> = {},
): void {
  if (!Array.isArray(draws) || draws.length > GRID_IMAGE_MAX_PLACEMENTS) {
    throw new RangeError('image draw list exceeds supported bounds')
  }
  let previousOrder = -1
  for (const draw of draws) {
    const source = draw?.source
    const rendererRepresentation = draw?.rendererRepresentation
    const hasSource = source !== undefined
    const hasRendererRepresentation = rendererRepresentation !== undefined
    if (draw === null || typeof draw !== 'object' ||
        !boundedInteger(draw.assetId, 1, 0xffffffff) ||
        !/^[0-9a-f]{64}$/u.test(draw.contentHash) ||
        hasSource === hasRendererRepresentation ||
        (options.requireSource === true && !hasSource) ||
        (hasSource && (source === null || typeof source !== 'object')) ||
        (hasRendererRepresentation &&
         (rendererRepresentation === null || typeof rendererRepresentation !== 'object')) ||
        !boundedInteger(draw.sourceWidthPixels, 1, GRID_IMAGE_MAX_DIMENSION) ||
        !boundedInteger(draw.sourceHeightPixels, 1, GRID_IMAGE_MAX_DIMENSION) ||
        (hasSource && (source.width !== draw.sourceWidthPixels ||
         source.height !== draw.sourceHeightPixels)) ||
        ![draw.sourceX, draw.sourceY, draw.sourceWidth, draw.sourceHeight,
          draw.destinationX, draw.destinationY, draw.destinationWidth, draw.destinationHeight,
          draw.u0, draw.v0, draw.u1, draw.v1].every(Number.isFinite) ||
        draw.sourceX < 0 || draw.sourceY < 0 || draw.sourceWidth <= 0 || draw.sourceHeight <= 0 ||
        !withinSourceBound(draw.sourceX, draw.sourceWidth, draw.sourceWidthPixels) ||
        !withinSourceBound(draw.sourceY, draw.sourceHeight, draw.sourceHeightPixels) ||
        draw.destinationWidth <= 0 || draw.destinationHeight <= 0 ||
        draw.u0 < 0 || draw.v0 < 0 || draw.u1 > 1 || draw.v1 > 1 ||
        draw.u0 >= draw.u1 || draw.v0 >= draw.v1 ||
        (draw.filter !== 'nearest' && draw.filter !== 'linear') ||
        !Number.isInteger(draw.order) || draw.order <= previousOrder) {
      throw new RangeError('image draw is invalid')
    }
    previousOrder = draw.order
  }
}

function withinSourceBound(origin: number, extent: number, bound: number): boolean {
  const tolerance = Number.EPSILON * Math.max(bound, Math.abs(origin), Math.abs(extent)) * 4
  return origin + extent <= bound + tolerance
}

function boundedInteger(value: number, minimum: number, maximum: number): boolean {
  return Number.isInteger(value) && value >= minimum && value <= maximum
}

function safeProduct(left: number, right: number): number {
  const result = left * right
  if (!Number.isSafeInteger(result)) throw new RangeError('image geometry multiplication is unsafe')
  return result
}

function roundHalfUp(numerator: number, denominator: number): number {
  const quotient = Math.trunc(numerator / denominator)
  const remainder = numerator % denominator
  return quotient + (remainder >= Math.ceil(denominator / 2) ? 1 : 0)
}

function validatePlacement(placement: DecodedImagePlacement, metrics: ImageGeometryMetrics): void {
  if (placement === null || typeof placement !== 'object' ||
      !boundedInteger(placement.assetId, 1, 0xffffffff) ||
      !boundedInteger(placement.x, 0, 0xffff) ||
      !boundedInteger(placement.y, 0, 0xffff) ||
      !boundedInteger(placement.width, 1, 0xffff) ||
      !boundedInteger(placement.height, 1, 0xffff) ||
      placement.x >= metrics.cols || placement.y >= metrics.rows ||
      (placement.fit !== 'contain' && placement.fit !== 'stretch') ||
      (placement.filter !== 'nearest' && placement.filter !== 'linear')) {
    throw new RangeError('image placement is invalid')
  }
}

function validateInputs(scene: DecodedImageSceneRecord, metrics: ImageGeometryMetrics): void {
  if (scene === null || typeof scene !== 'object' ||
      !boundedInteger(scene.assetEpoch, 1, 0xffffffff) ||
      !boundedInteger(scene.sceneGeneration, 1, 0xffffffff) ||
      !boundedInteger(scene.gridSequence, 1, 0xffffffff) ||
      !boundedInteger(scene.configGeneration, 1, 0xffffffff) ||
      !boundedInteger(scene.resizeGeneration, 1, 0xffffffff) ||
      !boundedInteger(metrics.cols, 1, GRID_COLS_MAX) ||
      !boundedInteger(metrics.rows, 1, GRID_ROWS_MAX) ||
      scene.cols !== metrics.cols || scene.rows !== metrics.rows ||
      !boundedInteger(metrics.cellWidth, 1, 0xffff) ||
      !boundedInteger(metrics.cellHeight, 1, 0xffff) ||
      !Number.isFinite(metrics.viewportWidth) || metrics.viewportWidth < 0 ||
      !Number.isFinite(metrics.viewportHeight) || metrics.viewportHeight < 0 ||
      !Number.isSafeInteger(metrics.cols * metrics.rows) ||
      !Array.isArray(scene.placements) || scene.placements.length > GRID_IMAGE_MAX_PLACEMENTS) {
    throw new RangeError('image geometry inputs are invalid')
  }
}

interface FitGeometry {
  readonly destinationX: number
  readonly destinationY: number
  readonly destinationWidth: number
  readonly destinationHeight: number
  readonly sourceX: number
  readonly sourceY: number
  readonly sourceWidth: number
  readonly sourceHeight: number
}

function fitGeometry(placement: DecodedImagePlacement, sourceWidth: number,
                     sourceHeight: number, metrics: ImageGeometryMetrics): FitGeometry | null {
  const rectangleX = safeProduct(placement.x, metrics.cellWidth)
  const rectangleY = safeProduct(placement.y, metrics.cellHeight)
  const rectangleWidth = safeProduct(placement.width, metrics.cellWidth)
  const rectangleHeight = safeProduct(placement.height, metrics.cellHeight)
  let fitX = rectangleX
  let fitY = rectangleY
  let fitWidth = rectangleWidth
  let fitHeight = rectangleHeight
  if (placement.fit === 'contain') {
    const widthScaledHeight = safeProduct(rectangleWidth, sourceHeight)
    const heightScaledWidth = safeProduct(rectangleHeight, sourceWidth)
    if (widthScaledHeight <= heightScaledWidth) {
      fitHeight = Math.max(1, roundHalfUp(widthScaledHeight, sourceWidth))
      fitY += Math.ceil((rectangleHeight - fitHeight) / 2)
    } else {
      fitWidth = Math.max(1, roundHalfUp(heightScaledWidth, sourceHeight))
      fitX += Math.ceil((rectangleWidth - fitWidth) / 2)
    }
  }
  const fitRight = fitX + fitWidth
  const fitBottom = fitY + fitHeight
  if (![fitX, fitY, fitRight, fitBottom].every(Number.isSafeInteger)) {
    throw new RangeError('image fit geometry is unsafe')
  }
  const clipLeft = fitX
  const clipTop = fitY
  const clipRight = Math.min(fitRight, metrics.viewportWidth)
  const clipBottom = Math.min(fitBottom, metrics.viewportHeight)
  if (clipLeft >= clipRight || clipTop >= clipBottom) return null
  const destinationWidth = clipRight - clipLeft
  const destinationHeight = clipBottom - clipTop
  return Object.freeze({
    destinationX: clipLeft,
    destinationY: clipTop,
    destinationWidth,
    destinationHeight,
    sourceX: (clipLeft - fitX) / fitWidth,
    sourceY: (clipTop - fitY) / fitHeight,
    sourceWidth: destinationWidth / fitWidth,
    sourceHeight: destinationHeight / fitHeight,
  })
}

function validateAsset(assetId: number, asset: ImageGeometryRenderAsset): void {
  const bitmap = 'bitmap' in asset ? asset.bitmap : undefined
  const rendererRepresentation = 'rendererRepresentation' in asset
    ? asset.rendererRepresentation
    : undefined
  const hasBitmap = bitmap !== undefined
  const hasRendererRepresentation = rendererRepresentation !== undefined
  if (asset.assetId !== assetId || !/^[0-9a-f]{64}$/u.test(asset.contentHash) ||
      !boundedInteger(asset.width, 1, GRID_IMAGE_MAX_DIMENSION) ||
      !boundedInteger(asset.height, 1, GRID_IMAGE_MAX_DIMENSION) ||
      hasBitmap === hasRendererRepresentation ||
      (hasBitmap && (bitmap === null || typeof bitmap !== 'object' ||
       bitmap.width !== asset.width || bitmap.height !== asset.height)) ||
      (hasRendererRepresentation &&
       (rendererRepresentation === null || typeof rendererRepresentation !== 'object'))) {
    throw new RangeError('ready image asset metadata is invalid')
  }
}

export function buildImageGeometry(scene: DecodedImageSceneRecord,
                                   assets: ImageGeometryAssetSource,
                                   metrics: ImageGeometryMetrics,
                                   options: BuildImageGeometryOptions = {}): ImageGeometry {
  validateInputs(scene, metrics)
  if (assets.activeEpoch !== undefined && assets.activeEpoch !== scene.assetEpoch) {
    throw new RangeError('image scene does not match the active asset epoch')
  }
  const draws: ImageDraw[] = []
  const suppression = new Uint8Array(metrics.cols * metrics.rows)
  const suppressionStride = metrics.cols + 1
  const suppressionDifference = new Int32Array(
    safeProduct(metrics.rows + 1, suppressionStride),
  )
  const leases: BrowserImageLease<ImageGeometryRenderAsset>[] = []
  const retainSources = options.retainSources ?? true
  let released = false
  const release = (): void => {
    if (released) return
    released = true
    for (const lease of leases) lease.release()
    leases.length = 0
  }
  try {
    for (let order = 0; order < scene.placements.length; order++) {
      const placement = scene.placements[order]
      validatePlacement(placement, metrics)
      const lease = assets.acquireForRender(placement.assetId)
      if (lease === null) continue
      try {
        validateAsset(placement.assetId, lease.value)
        const geometry = fitGeometry(placement, lease.value.width, lease.value.height, metrics)
        if (geometry === null) {
          lease.release()
          continue
        }
        const u0 = Math.max(0, Math.min(1, geometry.sourceX))
        const v0 = Math.max(0, Math.min(1, geometry.sourceY))
        const u1 = Math.max(u0, Math.min(1, geometry.sourceX + geometry.sourceWidth))
        const v1 = Math.max(v0, Math.min(1, geometry.sourceY + geometry.sourceHeight))
        const sourceX = u0 * lease.value.width
        const sourceY = v0 * lease.value.height
        const sourceWidth = (u1 - u0) * lease.value.width
        const sourceHeight = (v1 - v0) * lease.value.height
        const source = 'bitmap' in lease.value ? lease.value.bitmap : undefined
        const rendererRepresentation = 'rendererRepresentation' in lease.value
          ? lease.value.rendererRepresentation
          : undefined
        const draw = Object.freeze({
          assetId: placement.assetId,
          contentHash: lease.value.contentHash,
          ...(source === undefined ? {} : { source }),
          ...(rendererRepresentation === undefined ? {} : { rendererRepresentation }),
          sourceWidthPixels: lease.value.width,
          sourceHeightPixels: lease.value.height,
          sourceX,
          sourceY,
          sourceWidth,
          sourceHeight,
          u0,
          v0,
          u1,
          v1,
          destinationX: geometry.destinationX,
          destinationY: geometry.destinationY,
          destinationWidth: geometry.destinationWidth,
          destinationHeight: geometry.destinationHeight,
          filter: placement.filter,
          order,
        }) satisfies ImageDraw
        draws.push(draw)
        if (source === undefined || retainSources) leases.push(lease)
        else lease.release()
        const right = Math.min(metrics.cols, placement.x + placement.width)
        const bottom = Math.min(metrics.rows, placement.y + placement.height)
        const topLeft = placement.y * suppressionStride + placement.x
        const topRight = placement.y * suppressionStride + right
        const bottomLeft = bottom * suppressionStride + placement.x
        const bottomRight = bottom * suppressionStride + right
        suppressionDifference[topLeft]++
        suppressionDifference[topRight]--
        suppressionDifference[bottomLeft]--
        suppressionDifference[bottomRight]++
      } catch (error) {
        if (!leases.includes(lease)) lease.release()
        throw error
      }
    }
    for (let row = 0; row < metrics.rows; row++) {
      for (let col = 0; col < metrics.cols; col++) {
        const differenceIndex = row * suppressionStride + col
        let coverage = suppressionDifference[differenceIndex]
        if (row > 0) coverage += suppressionDifference[differenceIndex - suppressionStride]
        if (col > 0) coverage += suppressionDifference[differenceIndex - 1]
        if (row > 0 && col > 0) {
          coverage -= suppressionDifference[differenceIndex - suppressionStride - 1]
        }
        suppressionDifference[differenceIndex] = coverage
        suppression[row * metrics.cols + col] = coverage > 0 ? 1 : 0
      }
    }
    return Object.freeze({ draws: Object.freeze(draws), suppression, release })
  } catch (error) {
    release()
    throw error
  }
}

export const EMPTY_IMAGE_DRAWS: readonly ImageDraw[] = Object.freeze([])
